<template>
    <div class="baggage-content">
        <label v-if="showTitle" class="baggage-title">
            <Translation :translate="{ key: 'shared.alternatives' }" />
        </label>

        <div v-if="!split" class="baggage-content-inner">
            <div>
                <label v-if="showLabels && !isOneWay" class="baggage-sub-title">
                    <Translation :translate="{ key: 'shared.outbound_homebound' }" />
                </label>

                <List
                    :items="items"
                    :leg="LegType.RoundTrip"
                    :isOneWay="isOneWay"
                    :type="type"
                    :onSelection="onSelection"
                    :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
                    :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel"
                    :noBaggageSelectionLabel="noBaggageSelectionLabel"
                    :noBaggageOptionsLabel="getNoBaggageOptionsLabel(LegType.RoundTrip)" />
            </div>
            <div v-if="isTablet && showLabels && !isOneWay" />
        </div>

        <div v-else class="baggage-content-inner">
            <div>
                <label v-if="showLabels" class="baggage-sub-title">
                    <Translation :translate="{ key: 'shared.outbound_flight' }" />
                </label>
                <List
                    :items="outboundItems"
                    :leg="LegType.Outbound"
                    :isOneWay="isOneWay"
                    :type="type"
                    :onSelection="onSelection"
                    :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
                    :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel"
                    :noBaggageSelectionLabel="noBaggageSelectionLabel"
                    :noBaggageOptionsLabel="getNoBaggageOptionsLabel(LegType.Outbound)" />
            </div>
            <div>
                <label v-if="showLabels" class="baggage-sub-title">
                    <Translation :translate="{ key: 'shared.homebound' }" />
                </label>
                <List
                    :items="inboundItems"
                    :leg="LegType.Inbound"
                    :isOneWay="isOneWay"
                    :type="type"
                    :onSelection="onSelection"
                    :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
                    :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel"
                    :noBaggageSelectionLabel="noBaggageSelectionLabel"
                    :noBaggageOptionsLabel="getNoBaggageOptionsLabel(LegType.Inbound)" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { isEmpty } from 'lodash';
    import { PropType, defineComponent, computed } from 'vue';
    import {
        Baggage,
        BaggageIncluded,
        BaggageType,
        LegType,
    } from '@shared/interfaces/baggage';
    import {
        NoSelectionType,
        SelectionMetaData,
    } from '../../BaggageSelectionForm.interfaces';
    import { useScreenSize } from '@shared/helpers/viewport';
    import Translation from '@ui/components/Translation/Translation.vue';
    import List from './List.vue';

    export default defineComponent({
        name: 'Alternatives',
        components: { List, Translation },
        props: {
            items: { type: Array as PropType<Baggage[]>, default: () => [] },
            includedItems: {
                type: Array as PropType<BaggageIncluded[]>,
                default: () => [],
            },
            type: { type: String as PropType<BaggageType>, required: true },
            showLabels: { type: Boolean, default: false },
            showTitle: { type: Boolean, default: false },
            isOneWay: { type: Boolean, default: false },
            split: { type: Boolean, default: false },
            onSelection: {
                required: true,
                type: Function as PropType<
                    (
                        selection: Baggage | NoSelectionType,
                        ids: string[],
                        meta: SelectionMetaData
                    ) => void
                >,
            },
            noBaggageSelectionCabinBagLabel: { type: String, required: true },
            noBaggageSelectionCheckedBagLabel: { type: String, required: true },
            noBaggageSelectionLabel: { type: String, required: true },
        },
        setup(props) {
            const { isTablet } = useScreenSize();

            const outboundItems = computed((): Baggage[] =>
                getBaggagesBySector(props.items, 1)
            );

            const inboundItems = computed((): Baggage[] =>
                getBaggagesBySector(props.items, 2)
            );

            const getBaggagesBySector = (baggages: Baggage[], sector: number) =>
                [...baggages].filter((x: Baggage) =>
                    x.PerSector?.find((x) => x.SectorNumber === sector)
                );

            const outboundIncludedItems = computed((): BaggageIncluded[] =>
                getIncludedBaggagesBySector(props.includedItems, 1)
            );

            const inboundIncludedItems = computed((): BaggageIncluded[] =>
                getIncludedBaggagesBySector(props.includedItems, 2)
            );

            const getIncludedBaggagesBySector = (
                baggages: BaggageIncluded[],
                sector: number
            ) =>
                [...baggages].filter((x: BaggageIncluded) =>
                    x.PerSector?.find((x) => x.SectorNumber === sector)
                );

            const getNoBaggageOptionsLabel = (leg: LegType) => {
                const noBaggageOptionsLabel = t({
                    key: 'shared.no_baggage_options',
                });

                switch (leg) {
                    case LegType.Outbound: {
                        const showOutboundLabel =
                            !isEmpty(outboundIncludedItems.value) &&
                            isEmpty(outboundItems.value);
                        return showOutboundLabel ? noBaggageOptionsLabel : undefined;
                    }

                    case LegType.Inbound: {
                        const showInboundLabel =
                            !isEmpty(inboundIncludedItems.value) &&
                            isEmpty(inboundItems.value);
                        return showInboundLabel ? noBaggageOptionsLabel : undefined;
                    }

                    default:
                        return undefined;
                }
            };

            return {
                isTablet,
                LegType,
                outboundItems,
                inboundItems,
                getNoBaggageOptionsLabel,
            };
        },
    });
</script>
